
import { getStreakRank } from "@/lib/streakRanks";
import { StreakStorage, CookieStreakStorage } from "./storage/StreakStorageService";
import { StreakEventEmitter, DOMStreakEventEmitter, StreakState } from "./events/StreakEventService";
import { StreakCalculator } from "./streak/StreakCalculator";
import { getEffectiveDate, formatDate } from "@/lib/dateUtils";
import * as CookieManager from "@/lib/managers/CookieManager";

export class StreakManager {
  private static instance: StreakManager | null = null;
  private storage: StreakStorage;
  private eventEmitter: StreakEventEmitter;
  private calculator: StreakCalculator;
  private initialized: boolean = false;

  constructor(
    storage: StreakStorage = new CookieStreakStorage(),
    eventEmitter: StreakEventEmitter = new DOMStreakEventEmitter(),
    calculator: StreakCalculator = new StreakCalculator()
  ) {
    console.info('🏆 StreakManager initialized');
    this.storage = storage;
    this.eventEmitter = eventEmitter;
    this.calculator = calculator;
  }

  public static getInstance(): StreakManager {
    console.log('StreakManager getInstance called:', {
      hasInstance: !!StreakManager.instance,
      timestamp: new Date().toISOString()
    });
    
    if (!StreakManager.instance) {
      console.log('Creating new StreakManager instance');
      StreakManager.instance = new StreakManager();
    }

    if (!StreakManager.instance.initialized) {
      console.log('Initializing StreakManager instance');
      StreakManager.instance.initialize();
    }

    return StreakManager.instance;
  }

  private initialize(): void {
    if (this.initialized) return;
    
    try {
      // Trigger immediate legacy data migration before any other operations
      if (this.storage instanceof CookieStreakStorage) {
        (this.storage as any).ensureMigration();
      }
      
      // Get current values to initialize with
      const currentStreak = this.storage.getCurrentStreak();
      const maxStreak = this.storage.getMaxStreak();
      const lastWinDate = this.storage.getLastWinDate();
      
      console.info('🏆 Streak state:', { currentStreak, maxStreak, lastWinDate });

      // Initialize storage if empty
      if (currentStreak === null || currentStreak === undefined) {
        this.storage.setStreak(0);
      }
      if (maxStreak === null || maxStreak === undefined) {
        this.storage.setMaxStreak(0);
      }

      this.initialized = true;
    } catch (error) {
      console.error('Error initializing StreakManager:', error);
      this.initialized = false;
    }
  }

  public getStreakState(): StreakState {
    if (!this.initialized) {
      this.initialize();
    }

    const state = {
      currentStreak: this.storage.getCurrentStreak(),
      maxStreak: this.storage.getMaxStreak(),
      lastPlayed: this.storage.getLastPlayed()
    };
    
    return state;
  }

  public async handleGameCompletion(isWin: boolean, level: number): Promise<void> {
    if (!this.initialized) {
      this.initialize();
    }
    
    console.info('🏆 Processing game completion:', { isWin, level });
    
    
    // For wins, only process streak if level 4 is completed
    // For losses, process streak regardless of level
    if (isWin && level !== 4) {
      console.info('🏆 Win detected but not at level 4, skipping streak update');
      return;
    }

    try {
      // Get current game mode
      const currentMode = (localStorage.getItem('babblegram_game_mode') || 'daily') as 'daily' | 'practice' | 'custom';
      
      console.info('🏆 Current game mode for streak calculation:', { currentMode });
      
      // Handle practice mode streaks separately from daily streaks
      if (currentMode === 'practice') {
        console.info('🏆 Practice mode - handling separate practice streak');
        await this.handlePracticeStreak(isWin, level);
        return;
      }
      
      // For daily mode, continue with the existing logic
      if (currentMode !== 'daily') {
        console.info('🏆 Not in daily mode, skipping persistent streak update');
        return;
      }
      
      // Get all current values atomically first
      const today = formatDate(getEffectiveDate()); // Using getEffectiveDate instead of calculator.getCurrentDate()
      const currentStreak = this.storage.getCurrentStreak();
      const maxStreak = this.storage.getMaxStreak();
      const storedLastWinDate = this.storage.getLastWinDate();
      
      // Hardened fallback: if lastWinDate is missing but we have a non-zero streak, use lastPlayed
      // BUT ONLY if lastPlayed is not today (to avoid race conditions)
      let lastWinDate: string | null = storedLastWinDate;
      if (!lastWinDate && currentStreak > 0) {
        const lastPlayed = this.storage.getLastPlayed();
        if (lastPlayed && lastPlayed !== today) {
          console.info('🏆 Using lastPlayed as fallback lastWinDate:', {
            lastPlayed,
            today,
            reason: 'lastWinDate missing but lastPlayed is from previous day'
          });
          lastWinDate = lastPlayed;
        } else if (lastPlayed === today) {
          console.warn('🏆 Race condition detected: lastPlayed already set to today, keeping lastWinDate null:', {
            lastPlayed,
            today,
            currentStreak,
            reason: 'Preventing same-day fallback to avoid streak calculation issues'
          });
        }
      }
      
      console.info('🏆 Current streak state:', {
        currentStreak,
        maxStreak,
        lastWinDate,
        today,
        isWin,
        level,
        currentMode,
        debugMode: new URLSearchParams(window.location.search).get('debug') === 'date',
        debugDate: localStorage.getItem('debug_date')
      });

      // If it's a loss, immediately reset streak to 0
      if (!isWin) {
        console.info('🏆 Game lost - resetting streak to 0');
        
        // Store the previous streak value before resetting
        if (currentStreak > 0) {
          await this.storage.setPreviousStreak(currentStreak);
        }
        
        await this.storage.setStreak(0);
        await this.storage.setLastPlayed(today);
        await this.storage.setLastWinDate(null); // Reset last win date when streak is lost
        await this.storage.setPreviousStreak(0); // Reset previous streak to prevent "streak lost" from persisting
        
        // Emit the streak update event with 0
        this.eventEmitter.emitStreakUpdate({
          currentStreak: 0,
          maxStreak,
          lastPlayed: today
        });
        return;
      }

      // Calculate new streak values using the lastWinDate before any updates
      
      
      const { currentStreak: newStreak, maxStreak: newMaxStreak } = this.calculator.calculateNewStreak(
        isWin,
        currentStreak,
        maxStreak,
        lastWinDate,
        currentMode
      );

      console.info('🏆 Updated streak values:', {
        newStreak,
        newMaxStreak,
        change: newStreak - currentStreak,
        debugMode: new URLSearchParams(window.location.search).get('debug') === 'date',
        debugDate: localStorage.getItem('debug_date')
      });

      // Update all values sequentially to ensure proper order
      // First update streak values
      console.log('Updating streak storage:', { 
        oldStreak: currentStreak, 
        newStreak, 
        oldMax: maxStreak, 
        newMaxStreak, 
        today 
      });
      
      await this.storage.setStreak(newStreak);
      await this.storage.setMaxStreak(newMaxStreak);

      // Then update dates
      if (isWin) {
        await this.storage.setLastWinDate(today);
      }
      await this.storage.setLastPlayed(today);

      // Verify the values were saved correctly
      const verifyStreak = this.storage.getCurrentStreak();
      const verifyMaxStreak = this.storage.getMaxStreak();
      const verifyLastWin = this.storage.getLastWinDate();
      
      console.log('Verifying stored values:', {
        verifyStreak,
        verifyMaxStreak,
        verifyLastWin,
        expected: { newStreak, newMaxStreak, today }
      });

      

      // Finally emit the update
      this.eventEmitter.emitStreakUpdate({
        currentStreak: newStreak,
        maxStreak: newMaxStreak,
        lastPlayed: today
      });

    } catch (error) {
      console.error('Error in handleGameCompletion:', error);
      throw error;
    }
  }

  public getCurrentRank(): string {
    if (!this.initialized) {
      this.initialize();
    }
    return getStreakRank(this.storage.getCurrentStreak());
  }

  private async handlePracticeStreak(isWin: boolean, level: number): Promise<void> {
    try {
      // Get practice mode specific streak from localStorage
      const practiceStreakKey = 'babblegram_practice_streak';
      const currentPracticeStreak = parseInt(localStorage.getItem(practiceStreakKey) || '0', 10);
      
      console.info('🏆 Practice streak before update:', { currentPracticeStreak, isWin, level });
      
      let newPracticeStreak = currentPracticeStreak;
      
      const practicePreviousStreakKey = 'babblegram_practice_previous_streak';

      if (isWin && level === 4) {
        // Increment practice streak on level 4 completion
        newPracticeStreak = currentPracticeStreak + 1;
        // Clear any previous lost streak marker on a fresh win
        localStorage.setItem(practicePreviousStreakKey, '0');
      } else if (!isWin) {
        // Remember the streak we are about to lose so the UI can decide whether to show it
        if (currentPracticeStreak > 0) {
          localStorage.setItem(practicePreviousStreakKey, currentPracticeStreak.toString());
        }
        // Reset practice streak on loss
        newPracticeStreak = 0;
      }
      
      // Store the new practice streak
      localStorage.setItem(practiceStreakKey, newPracticeStreak.toString());
      
      console.info('🏆 Practice streak after update:', { newPracticeStreak });
      
      // Emit update event for practice mode
      this.eventEmitter.emitStreakUpdate({
        currentStreak: newPracticeStreak,
        maxStreak: Math.max(newPracticeStreak, parseInt(localStorage.getItem('babblegram_practice_max_streak') || '0', 10)),
        lastPlayed: formatDate(getEffectiveDate())
      });
      
      // Update max practice streak if needed
      const maxPracticeStreak = parseInt(localStorage.getItem('babblegram_practice_max_streak') || '0', 10);
      if (newPracticeStreak > maxPracticeStreak) {
        localStorage.setItem('babblegram_practice_max_streak', newPracticeStreak.toString());
      }
      
    } catch (error) {
      console.error('Error handling practice streak:', error);
    }
  }

  public subscribeToStreakUpdates(callback: (streak: number) => void): () => void {
    if (!this.initialized) {
      this.initialize();
    }
    console.log('StreakManager - Subscribing to streak updates');
    return this.eventEmitter.subscribeToStreakUpdates(callback);
  }
}

// Initialize the singleton after the class definition
let streakManagerInstance: StreakManager | null = null;

export const getStreakManager = (): StreakManager => {
  if (!streakManagerInstance) {
    console.log('Creating initial streak manager instance');
    streakManagerInstance = StreakManager.getInstance();
  }
  return streakManagerInstance;
};

export const streakManager = getStreakManager();
